import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import Select from "react-dropdown-select";
import { Formik } from "formik";
import { useHistory } from "react-router-dom";

import { useModelPopup } from '../../../../common/hooks/index';
import { Button, BtnType, Label, TextBox, TextArea, MultiSelect } from "modules/primitives";
import { ModalBody, ModalFooter } from "application/modal";
import {
  replicateProjectModel,
  replicateProjectFormValidation,
} from "modules/projects/project/replicateProject/replicateProject.constant";
import { getPlantsAndAreas } from "modules/admin/adminPlantsAndAreas/adminPlantsAndAreas.actions";
import ReplicationProjectThemes from "modules/projects/project/replicateProject/replicationProjectThemes";
import { httpPost } from "../../../../common/httpCall";
import { makeStages } from "../../../../common/helper";
import PlantMultiSelect from "../../../../common/components/plantMultiselect";
import NewProjectEvaluation from "../../../../common/components/newProjectSharedComponents/newProjectEvaluation";
import UserMultiSelectSearch from "../../../../common/components/usersSearch";
import LoadingComponent from "modules/primitives/loading/loading.component";
import { getProjectReplicationStatus } from "modules/projects/project/project.action";

const stages = makeStages(new Date().getFullYear());

const ReplicateProject = ({
  project,
  fieldsStuff,
  isThemesEnabled,
}) => {
  const [model, setModel] = useState({
    ...replicateProjectModel,
    ...fieldsStuff?.formModel
  });
  const [isFetching, setIsFetching] = useState(false);
  const history = useHistory();
  const popup = useModelPopup();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const {
    councilsReducer: { councilData },
    adminPlantsAndAreasReducer: { plantsAndAreas },
    stepOneReducer: { stageTemplates },
    projectsReducer: {
      projectReducer: { projectPlant, replicationData },
    },
  } = useSelector((state) => state);

  const updateModel = newModel => {
    setModel({
      ...model,
      ...newModel
    });
  }

  const getPlantsAsOptions = useCallback((applyFilters) => {
    let plantsArray = plantsAndAreas.map(plant => ({
      id: plant.id,
      name: plant.attributes.name,
      areas: plant.attributes.areas,
    }));

    if (project?.original_project?.plant) {
      const areaIdsFromOriginalProj = project?.original_project?.plant?.areas
        ? project?.original_project?.plant.areas.map(a => a.id)
        : [];

      plantsArray = plantsArray
        .map(p => {
          if (!areaIdsFromOriginalProj.length) return p;

          return {
            ...p,
            areas: p.areas.filter(a => !areaIdsFromOriginalProj.includes(a.area_id)),
          }
        })
    }

    if (projectPlant) {
      const areaIds = projectPlant?.areas
        ? projectPlant.areas.map(a => a.id)
        : [];

      plantsArray = plantsArray
        .map(p => {
          if (!areaIds.length || p.id !== projectPlant.id) return p;

          return {
            ...p,
            areas: p.areas.filter(a => !areaIds.includes(a.area_id)),
          }
        })
        .filter(p => {
          // it means we have already selected all areas for that plant, so we don't want
          // to see that plant anymore
          if (p.id === projectPlant.id && applyFilters) {
            return p.areas.length > 0;
          }

          return true;
        });
    }

    if (applyFilters && (replicationData?.project_replications?.length || replicationData?.original_project?.plant?.id)) {
      let originalPlant = [];

      if (replicationData?.original_project?.plant?.id) {
        originalPlant = [{
          ...replicationData.original_project?.plant,
          areas: [{
            ...replicationData.original_project?.plant.area,
            area_id: replicationData.original_project?.plant.area.id
          }]
        }];
      }

      const combinedData = [
        ...originalPlant,
        ...replicationData?.project_replications || []
      ];

      plantsArray = plantsArray.map(plant => {
        const getPlantsById = combinedData.filter(p => p.id === plant.id);

        if (getPlantsById?.length) {
          const allAreasIds = getPlantsById
            .reduce((acc, plant) => [...acc, ...plant.areas || []], [])
            .map(area => area.area_id);

          if (allAreasIds.length) {
            return {
              ...plant,
              areas: plant.areas.filter(a => !allAreasIds.includes(a.area_id))
            }
          }

          return {
            ...plant,
            filterPlant: allAreasIds.length === plant.areas.length || !plant.areas.length,
          }
        }

        return plant;
      }).filter(p => !p.filterPlant);
    }

    return plantsArray;
  }, [plantsAndAreas, project, projectPlant, replicationData]);

  const makeModel = useCallback(() => {
    const splitName = project.name.replace(/\(Replication \d+(\.\d+)*\)/, '');
    const parser = new DOMParser();
    const doc = parser.parseFromString(project.description || '', 'text/html');
    const clearDescription = doc.body.textContent || '';
    let props = {
      name: `${splitName} (Replication ${project.next_replication_number})`,
      description: clearDescription,
    };

    if (project.themes?.length) {
      props = {
        ...props,
        themes: project.themes.map(t => ({...t, id: t.theme_id})),
      }
    }

    if (stageTemplates) {
      const stage_template = stageTemplates
        .find((template) => template.id === project.stage_template_id);

      props = {
        ...props,
        stage_template_id: stage_template?.id || '',
      }
    }

    updateModel(props);
  }, [project, model, stageTemplates]);

  useEffect(() => {
    if (!plantsAndAreas.length) {
      dispatch(
        getPlantsAndAreas({
          enqueueSnackbar,
        })
      );
    } else {
      makeModel();
    }
  }, [plantsAndAreas]);

  const handleDismiss = () => {
    popup.hide();
  };

  const submit = (values) => {
    if (
      values.selected_plant?.id &&
      values.selected_plant?.areas?.length > 0 &&
      !values.selected_plant?.selected_areas?.length
    ) return;

    setIsFetching(true);

    const {
      next_stage_change,
      themes,
      selected_plant,
      selectedPlant1,
      selectedAreas1,
      name,
      description,
      project_owners,
      stage_template_id,
      ...rest
    } = values;
    const getValueByType = (value) => {
      if (typeof value === 'string') return value
      if (Array.isArray(value)) return value.map(el => el.id);
      return value;
    };
    let payload;

    if (councilData?.settings?.project_version_path === 'v3') {
      payload = {
        description,
        next_stage_change: next_stage_change?.date || '',
        ford_custom_stuff: {
          next_stage_change: next_stage_change?.date || '',
          possible_plant_ids: selectedPlant1?.map(plant => plant.id) || [],
          possible_area_ids: selectedAreas1?.map(a => a.area_id) || [],
          area_ids: selected_plant?.selected_areas?.map(a => a.area_id) || [],
          plant_id: selected_plant?.id,
          original_project_id: project.id,
          custom_project_fields: Object.keys(rest).map(key => {
            const getOriginalField = fieldsStuff.fieldsList.find(field => (field.id || field.project_field_template_id) === key);

            return {
              field_name: getOriginalField.field_name,
              field_value: getValueByType(rest[key]),
            }
          }),
        },
        name,
        project_owner_ids: project_owners?.map(o => o.user_id || o.id) || [],
        stage_template_id,
        theme_ids: themes?.map(o => o.theme_id) || [],
        project_metric_attributes: {
          next_stage_end_on: next_stage_change?.date || '',
        },
      }
    } else {
      payload = {
        resource_id: project.id,
        resource_type: "project",
        project_fields: Object.keys(rest).map(key => {
          const getOriginalField = fieldsStuff.fieldsList.find(field => (field.id || field.project_field_template_id) === key);

          return {
            field_id: key,
            selected_values: getValueByType(rest[key]),
            project_field_template_id: getOriginalField?.project_field_template_id
          }
        }),
        themes,
        selected_plant,
        name,
        description,
        next_stage_change: next_stage_change?.date || '',
        possible_plant_ids: selectedPlant1?.map(plant => plant.id) || [],
        possible_area_ids: selectedAreas1?.map(a => a.area_id) || [],
        project_owners: project_owners?.map(o => o.user_id || o.id) || [],
        stage_template_id,
      };
    }

    httpPost({
      apiVersion: councilData?.settings?.project_version_path || 'v1',
      call: `replications`,
      data: payload,
    }).subscribe((res) => {
      setIsFetching(false);
      if (res.response?.id) {
        popup.hide();
        enqueueSnackbar('Successfully created replication for project.', {
          variant: 'success',
        });
        dispatch(getProjectReplicationStatus({
          id: res.response?.id
        }));
        history.push(`/projects/${res.response?.id}`);
      }
    })
  };

  const filterPlantsByProp = useCallback((prop, values) => {
    const makePlants = getPlantsAsOptions(prop !== 'selected_plant');

    if (prop === 'selected_plant') {
      if (!values.selected_plant) {
        return makePlants;
      }

      return makePlants.filter(plant => plant.id !== values.selected_plant.id);
    }

    const existingIds = values.selectedPlant1?.map(p => p.id) || [];

    if (!existingIds?.length) {
      return makePlants
    }

    return makePlants.filter(plant => !existingIds.includes(plant.id));
  }, [getPlantsAsOptions, projectPlant, replicationData]);

  const checkCreateBtnIsDisabled = (values) => {
    if (!values.selected_plant) return false;

    return values.selected_plant?.areas?.length > 0 && values.selected_plant?.selected_areas?.length === 0;
  };

  return (
    <>
      <Formik
        initialValues={model}
        enableReinitialize
        validationSchema={replicateProjectFormValidation}
        onSubmit={submit}
      >
        {({ handleSubmit, setFieldValue, values, ...formikprops }) => (
          <>
            <ModalBody>
              <div className="d-flex flex-column w-100">
                <Label>Project name</Label>
                <TextBox
                  type="text"
                  name="name"
                  placeholder="Project name"
                  formProps={{
                    values,
                    setFieldValue,
                    ...formikprops,
                  }}
                  className="w-100"
                />
              </div>
              <NewProjectEvaluation
                formikprops={{
                  values,
                  setFieldValue,
                  ...formikprops,
                }}
              />
              <div className="mb-3">
                <Label>Plant</Label>
                <Select
                  multi={false}
                  closeOnSelect
                  values={values.selected_plant ? [values.selected_plant] : []}
                  onChange={(val) => {
                    setFieldValue("selected_plant", val[0]);
                  }}
                  options={filterPlantsByProp("selectedPlant1", values)}
                  labelField="name"
                  valueField="id"
                  placeholder="Plants"
                  searchBy="name"
                />
                {
                  formikprops.touched["selected_plant"] &&
                  formikprops.errors["selected_plant"] ? (
                    <p className="text-danger small">
                      {formikprops.errors["selected_plant"]}
                    </p>
                  ) : null
                }
                {
                  values.selected_plant?.id && (
                    <div className="d-flex justify-content-end">
                      <span
                        className="text-muted small cursor-pointer"
                        onClick={() => setFieldValue("selected_plant", null)}
                      >clear</span>
                    </div>
                  )
                }
              </div>
              {
                values.selected_plant?.areas?.length > 0 ? (
                  <div className="mt-3 mb-3">
                    <Label>Areas</Label>
                    <Select
                      multi={false}
                      closeOnSelect
                      values={values.selected_plant?.selected_areas || []}
                      onChange={(val) => {
                        setFieldValue("selected_plant", {
                          ...values.selected_plant,
                          selected_areas: val,
                        });
                      }}
                      options={values.selected_plant.areas}
                      labelField="area_name"
                      valueField="area_id"
                      placeholder="Areas"
                      searchBy="area_name"
                    />
                    {
                      !values.selected_plant?.selected_areas?.length ? (
                        <span className="text-danger d-block mt-2">Please select an Area</span>
                      ) : null
                    }
                  </div>
                ) : null
              }
              <div className="mb-3">
                <PlantMultiSelect
                  formikprops={{
                    values,
                    setFieldValue,
                    ...formikprops,
                  }}
                  plants={filterPlantsByProp("selected_plant", values)}
                />
              </div>
              {
                isThemesEnabled && (
                  <ReplicationProjectThemes
                    handleSelect={(themes) => setFieldValue("themes", themes)}
                    themes={values.themes}
                  />
                )
              }
              <div className="d-flex flex-column w-100">
                <Label>Description</Label>
                <TextArea
                  type="text"
                  name="description"
                  placeholder="Description"
                  className="w-100"
                  formProps={{
                    values,
                    setFieldValue,
                    ...formikprops,
                  }}
                  rows={3}
                />
              </div>
              {
                fieldsStuff?.fieldsList?.map(field => {
                  const useId = field.id || field.project_field_template_id;

                  return (
                    <div key={useId}>
                      {
                        field.field_type === "text" ? (
                          <div>
                            <Label>{field.field_name}</Label>
                            {
                              field.big_box_text ? (
                                <textarea
                                  className="w-100 border small p-2"
                                  value={values[useId]}
                                  placeholder={field.field_placeholder || field.field_name}
                                  onChange={(evt) => setFieldValue(useId, evt.target.value)}
                                  rows="3"
                                ></textarea>
                              ) : (
                                <TextBox
                                  name={useId}
                                  placeholder={field.field_placeholder || field.field_name}
                                  formProps={{
                                    values,
                                    setFieldValue,
                                    ...formikprops,
                                  }}
                                  className="w-100"
                                  value={values[useId]}
                                />
                              )
                            }
                          </div>
                        ) : null
                      }
                      {
                        field.field_type === "dropdown" ? (
                          <div>
                            <Label>{field.field_name}</Label>
                            <MultiSelect
                              name={useId}
                              values={values[useId]}
                              labelField="name"
                              valueField="id"
                              onChange={val => setFieldValue(useId, val)}
                              options={field.option_values}
                              multi={field.multiselect}
                              placeholder={field.field_placeholder || field.field_name}
                            />
                            {
                              values[useId] && field.isClearable && (
                                <div className="d-flex justify-content-end" style={{ marginTop: "-10px" }}>
                                <span
                                  className="text-muted small cursor-pointer"
                                  onClick={() => setFieldValue(useId, null)}
                                >clear</span>
                                </div>
                              )
                            }
                          </div>
                        ) : null
                      }
                    </div>
                  );
                })
              }
              <div className="mb-3">
                <Label>Next Stage Change</Label>
                <Select
                  label="Next Stage Change"
                  placeholder="Next Stage Change"
                  labelField="name"
                  valueField="id"
                  name="next_stage_change"
                  multi={false}
                  options={stages}
                  onChange={(val) => {
                    setFieldValue("next_stage_change", val[0]);
                  }}
                />
              </div>
              <div className="mb-3">
                <Label>Select Project Owner</Label>
                <UserMultiSelectSearch
                  multi={false}
                  placeholder="Search for project owner"
                  labelField="full_name"
                  valueField="id"
                  searchBy="full_name"
                  hasAll={false}
                  onChange={(value) => {
                    setFieldValue("project_owners", value);
                  }}
                  preselectCurrentUser
                />
              </div>
            </ModalBody>
            <ModalFooter>
              {
                isFetching ? (
                  <LoadingComponent customText="One moment. This Project is being Replicated." />
                ) : (
                  <>
                    <Button btn={BtnType.OUTLINE} onClick={handleDismiss}>
                      Cancel
                    </Button>
                    <Button
                      disabled={checkCreateBtnIsDisabled(values)}
                      onClick={handleSubmit}
                      btn={BtnType.REGULAR}
                      className="ml-2"
                      type="submit"
                    >
                      Create
                    </Button>
                  </>
                )
              }
            </ModalFooter>
          </>
        )}
      </Formik>
    </>
  );
};

export default ReplicateProject;
